import { NavLink } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import { toggleNavCollapsed } from "@app/slices/prefsSlice";
import { useLocale } from "@hooks/useLocale";
import { isGlobalAdmin } from "@api/brokerAuth";
import { TENANT_BRANDS } from "@oxbo/tokens";
import type { MessageKey } from "@i18n/messages";
import { WellSwitcher } from "./WellSwitcher";
import { SettingsMenu } from "./SettingsMenu";

/** Left nav mirroring the sitemap. Icons are simple glyphs (no icon dep). */
interface NavItem {
  to: string;
  labelKey: MessageKey;
  icon: string;
}

const ITEMS: NavItem[] = [
  { to: "/wells", labelKey: "nav.wellExplorer", icon: "⌖" },
  { to: "/well", labelKey: "nav.wellRecord", icon: "▤" },
  { to: "/daily", labelKey: "nav.dailyReport", icon: "☰" },
  { to: "/time-npt", labelKey: "nav.timeNpt", icon: "⏱" },
  { to: "/cost-afe", labelKey: "nav.costAfe", icon: "$" },
  { to: "/bha", labelKey: "nav.bha", icon: "⚙" },
  { to: "/tubulars", labelKey: "nav.tubulars", icon: "▮" },
  { to: "/mud", labelKey: "nav.mud", icon: "≋" },
  { to: "/hse", labelKey: "nav.hse", icon: "⛑" },
  { to: "/surveys", labelKey: "nav.surveys", icon: "∿" },
  { to: "/inbox", labelKey: "nav.inbox", icon: "✉" },
  { to: "/saved", labelKey: "nav.savedReports", icon: "★" },
  { to: "/export", labelKey: "nav.export", icon: "⤓" },
];

/** Admin-only nav items — rendered only for RCI global super-admins. */
const ADMIN_ITEMS: NavItem[] = [
  { to: "/admin/picklists", labelKey: "nav.picklists", icon: "🏷" },
];

/**
 * Left nav — the full app chrome now the top header is gone. Hosts the tenant
 * wordmark, the well switcher, the sitemap links, a collapse/expand toggle, and
 * (pinned to the bottom) the Settings cogwheel. Collapsed = ~56px icon rail with
 * hover tooltips; expanded = ~220px icon + label. Collapsed state is a persisted
 * user preference.
 */
export function LeftNav() {
  const { t } = useLocale();
  const dispatch = useAppDispatch();
  const collapsed = useAppSelector((s) => s.prefs.navCollapsed);
  const tenant = useAppSelector((s) => s.prefs.tenant);
  const brand = TENANT_BRANDS[tenant];
  // Admin-only nav (e.g. Picklist Admin) is rendered only for RCI global
  // super-admins; non-admins never see it (the backend also 403s mutations).
  const items = isGlobalAdmin() ? [...ITEMS, ...ADMIN_ITEMS] : ITEMS;

  return (
    <nav
      className={`leftnav${collapsed ? " collapsed" : ""}`}
      aria-label="Sections"
      data-collapsed={collapsed}
    >
      <div className="leftnav-top">
        <div className="wordmark" title={collapsed ? brand.wordmark : undefined}>
          {brand.logoSrc ? (
            <img
              className="mark mark-logo"
              src={brand.logoSrc}
              alt={`${brand.wordmark} logo`}
            />
          ) : (
            <span className="mark" aria-hidden="true">
              {brand.initial}
            </span>
          )}
          {!collapsed && <span className="wordmark-text">{brand.wordmark}</span>}
        </div>

        <WellSwitcher collapsed={collapsed} />
      </div>

      <div className="leftnav-links">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) => (isActive ? "active" : "")}
            title={collapsed ? t(item.labelKey) : undefined}
          >
            <span className="nav-icon" aria-hidden="true">
              {item.icon}
            </span>
            <span className="nav-label">{t(item.labelKey)}</span>
            {collapsed && <span className="nav-tooltip" role="tooltip">{t(item.labelKey)}</span>}
          </NavLink>
        ))}
      </div>

      <div className="leftnav-bottom">
        <button
          type="button"
          className="nav-collapse-toggle"
          aria-expanded={!collapsed}
          aria-label={collapsed ? t("nav.expand") : t("nav.collapse")}
          title={collapsed ? t("nav.expand") : t("nav.collapse")}
          onClick={() => dispatch(toggleNavCollapsed())}
        >
          <span className="chev" aria-hidden="true">
            {collapsed ? "»" : "«"}
          </span>
          {!collapsed && <span className="nav-label">{t("nav.collapse")}</span>}
        </button>

        <SettingsMenu collapsed={collapsed} />
      </div>
    </nav>
  );
}
